import { apiEndpoints } from '../../utils/apiService';

/**
 * Event and catering inquiries are the same backend shape
 * (event_inquiries/models.py: EventInquiry, CateringInquiry) behind two sets
 * of endpoints. Everything that differs between the two pages lives here.
 */
export type InquiryKind = 'event' | 'catering';

export interface InquiryKindConfig {
  kind: InquiryKind;
  /** Page title. */
  title: string;
  /** One line under the title. */
  description: string;
  /** What a record is called in headings: "Event", "Catering". */
  recordLabel: string;
  /** Fallback headline when the occasion wasn't captured. */
  occasionFallback: string;
  labels: {
    occasion: string;
    date: string;
    time: string;
    guests: string;
    guestCount: string;
  };
  searchPlaceholder: string;
  emptyAll: string;
  endpoints: {
    list: (query: Record<string, string>) => string;
    detail: (id: number) => string;
    update: (id: number) => string;
    dashboardState: (id: number) => string;
    summary: () => string;
    markAllRead: () => string;
  };
}

export const EVENT_INQUIRIES: InquiryKindConfig = {
  kind: 'event',
  title: 'Events',
  description: '',
  recordLabel: 'Event',
  occasionFallback: 'Event',
  labels: {
    occasion: 'Occasion',
    date: 'Event date',
    time: 'Time',
    guests: 'Guests',
    guestCount: 'Guest count',
  },
  searchPlaceholder: 'Search guests, phone, occasion…',
  emptyAll:
    'When a guest asks Arthur about hosting an event, it shows up here with a summary and transcript.',
  endpoints: {
    list: (query) => apiEndpoints.eventInquiryList(query),
    detail: (id) => apiEndpoints.getEventInquiryDetail(id),
    update: (id) => apiEndpoints.updateEventInquiry(id),
    dashboardState: (id) => apiEndpoints.eventInquiryDashboardState(id),
    summary: () => apiEndpoints.getEventInquiryDashboardSummary(),
    markAllRead: () => apiEndpoints.eventInquiryMarkAllRead(),
  },
};

export const CATERING_INQUIRIES: InquiryKindConfig = {
  kind: 'catering',
  title: 'Catering',
  description: '',
  recordLabel: 'Catering',
  occasionFallback: 'Catering',
  labels: {
    occasion: 'Catering type',
    date: 'Service date',
    time: 'Service time',
    guests: 'Headcount',
    guestCount: 'Headcount',
  },
  searchPlaceholder: 'Search guests, phone, catering type…',
  emptyAll:
    'When a guest asks Arthur about catering, it shows up here with a summary and transcript.',
  endpoints: {
    list: (query) => apiEndpoints.cateringInquiryList(query),
    detail: (id) => apiEndpoints.getCateringInquiryDetail(id),
    update: (id) => apiEndpoints.updateCateringInquiry(id),
    dashboardState: (id) => apiEndpoints.cateringInquiryDashboardState(id),
    summary: () => apiEndpoints.getCateringInquiryDashboardSummary(),
    markAllRead: () => apiEndpoints.cateringInquiryMarkAllRead(),
  },
};
